const PUBLIC_CONT_SUFFIXES = [
  '/cont/login',
  '/cont/register',
  '/cont/forgot-password',
  '/cont/reset-password',
]

/**
 * Auth is cookie-based but admin/auth middleware stays client-only (no user on SSR).
 * A document navigation avoids swapping admin → default layout in-place.
 * ESC / Stop aborts that load and would leave /admin on screen — fall back to SPA.
 */
export function navigateToLogin(redirect?: string | null) {
  const shopPath = useShopPath()
  const to = {
    path: shopPath('/cont/login'),
    query: redirect ? { redirect } : undefined,
  }

  if (import.meta.client) {
    const onAbortKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      window.removeEventListener('keydown', onAbortKey)
      void navigateTo(to, { replace: true })
    }
    window.addEventListener('keydown', onAbortKey)
    window.setTimeout(() => window.removeEventListener('keydown', onAbortKey), 4000)
  }

  return navigateTo(to, { external: true, replace: true })
}

function isPublicContPath(path: string): boolean {
  return PUBLIC_CONT_SUFFIXES.some(
    (suffix) => path === suffix || path.endsWith(suffix),
  )
}

export { isPublicContPath }

export function defaultAuthenticatedPath(isAdmin: boolean): string {
  return isAdmin ? '/admin' : '/cont'
}

export function resolveAuthRedirect(isAdmin: boolean, redirect?: string | null): string {
  if (redirect?.startsWith('/') && !isPublicContPath(redirect)) {
    if (isAdmin && (redirect.includes('/cont/') || redirect.endsWith('/cont'))) {
      // Prefer staying in same shop prefix when present
      const match = redirect.match(/^\/([^/]+)\//)
      if (match && match[1] !== 'cont' && match[1] !== 'admin') {
        return `/${match[1]}/admin`
      }
      return '/admin'
    }
    if (!isAdmin && redirect.includes('/admin')) {
      const match = redirect.match(/^\/([^/]+)\//)
      if (match && match[1] !== 'cont' && match[1] !== 'admin') {
        return `/${match[1]}/cont`
      }
      return '/cont'
    }
    return redirect
  }

  return defaultAuthenticatedPath(isAdmin)
}

export function isCustomerContPath(path: string): boolean {
  return (
    (path.startsWith('/cont') || path.includes('/cont/'))
    && !isPublicContPath(path)
  )
}
